import { format } from 'date-fns';
import { map, pickBy } from 'lodash';
import type { ResultOkType } from 'ts-results-es';
import { z } from 'zod';

import { type BodyOf, FeApi } from '..';
import {
  SitePreviewExtendedSchema,
  SitePreviewSchema,
  SiteSchema,
} from '../domain/entities/site';
import type { ISiteRepository } from '../domain/repositories/site.repository';

import { sitePreviewExtendedMapper } from './entities/site-preview-extended.mapper';
import { sitePreviewMapper } from './entities/site-preview.mapper';
import { siteMapper } from './entities/site.mapper';

export namespace SiteRepositoryMapper {
  export namespace getAll {
    export const outputSchema = z.array(SitePreviewSchema);

    export function outputFromData(
      data: FeApi.Sites,
    ): Promise<ISiteRepository.GetAllOutput> {
      return outputSchema.parseAsync(map(data.sites, sitePreviewMapper));
    }
  }

  export namespace getAllExtended {
    export const outputSchema = z.array(SitePreviewExtendedSchema);

    export function outputFromData(
      data: FeApi.Sites,
    ): Promise<ISiteRepository.GetAllExtendedOutput> {
      return outputSchema.parseAsync(
        map(data.sites, sitePreviewExtendedMapper),
      );
    }
  }

  export namespace getById {
    export const outputSchema = SiteSchema;
    export function outputFromData(
      data: FeApi.GetSiteDetailV2ViewOutput,
    ): Promise<ResultOkType<ISiteRepository.GetByIdOutput>> {
      return outputSchema.parseAsync(siteMapper(data));
    }

    export function bodyFromInput(
      input: ISiteRepository.GetByIdInput,
    ): BodyOf<typeof FeApi.customerInfosV2SitesDetailRetrieve> {
      return {
        accureId: input.id ?? '',
        includePcsAlerts: input.includePcsAlerts,
      };
    }
  }

  export namespace update {
    export function bodyFromInput(
      input: ISiteRepository.UpdateInput,
    ): BodyOf<typeof FeApi.customerInfosV1SitesUpdateUpdate> {
      const updateRequest = {
        ...(input.picture
          ? {
              picture: input.picture,
            }
          : {}),
        operational_phase: input.operationalPhase,
        address: input.address,
        latitude: input.latitude,
        longitude: input.longitude,
        nominal_energy_wh: input.nominalEnergyWh,
        nominal_power_w: input.nominalPowerW,
        installation_date: input.installationDate
          ? format(input.installationDate, 'yyyy-MM-dd')
          : undefined,
        commissioning_date: input.commissioningDate
          ? format(input.commissioningDate, 'yyyy-MM-dd')
          : undefined,
      };

      return {
        accureId: input.id,
        updateSiteViewInputRequest: pickBy(
          updateRequest,
          (value) => value !== undefined,
        ),
      };
    }
  }

  export namespace create {
    export function bodyFromInput(
      input: ISiteRepository.CreateInput,
    ): BodyOf<typeof FeApi.customerInfosV1SitesNewCreate> {
      return {
        createSiteViewInputRequest: {
          name: input.name,
          operational_phase: input.operationalPhase,
          address: input.address,
          latitude: input.lat,
          longitude: input.lng,
          nominal_energy_wh: input.nominalEnergyWh,
          nominal_power_w: input.nominalPowerW,
          installation_date: format(input.installationDate, 'yyyy-MM-dd'),
          commissioning_date: format(input.commissioningDate, 'yyyy-MM-dd'),
          ...(input.picture
            ? {
                picture: input.picture,
              }
            : {}),
        },
      };
    }
  }
}
